import { Trans } from '@lingui/macro'
import { Stack } from '@mui/material'
import type { Model } from 'jms-domain'
import { useMemo } from 'react'

import {
  AttributeCardSelector,
  AttributeSwitchSelector,
  DistanceDimensionInput,
  useResolveLocalizable,
} from '@salsita-ai/platform-project-sdk/react'
import { PropertyContainer } from '@salsita-ai/platform-project-sdk/ui'

import { configuratorInterface } from '../systems/configurator-interface'

interface RoomLayoutProps {
  id: Model.Room['id']
  wallIds: Array<Model.Wall['id']>
}

export const RoomLayout = (props: RoomLayoutProps) => {
  const { id, wallIds } = props

  const { domain } = configuratorInterface.useDomain()
  const state = configuratorInterface.useCommittedState()
  const $walls = domain.get$Entities(wallIds, state)
  const resolveLocalizable = useResolveLocalizable()

  const activeWalls = useMemo(() => $walls.filter(wall => wall.$active), [$walls])

  return (
    <PropertyContainer.Group spacing="large">
      <PropertyContainer.Item
        title={<Trans>Layout</Trans>}
        description={<Trans>Select the space the cabinets will be placed</Trans>}
      >
        <AttributeCardSelector entityId={id} instanceName="shape" />
      </PropertyContainer.Item>

      <PropertyContainer.Item
        title={<Trans>Dimensions</Trans>}
        description={<Trans>Specify the dimensions of the room</Trans>}
      >
        <Stack
          direction="row"
          spacing={2}
          sx={() => ({
            mt: 3,
            '& > *': {
              flex: 1,
            },
          })}
        >
          {activeWalls.map(wall => (
            <DistanceDimensionInput key={wall.id} entityId={wall.id} instanceName="length" inputLabel={wall.$name} />
          ))}
        </Stack>
      </PropertyContainer.Item>

      <PropertyContainer.Item
        title={<Trans>Walls</Trans>}
        description={<Trans>Disable to remove the wall behind cabinets</Trans>}
      >
        {activeWalls.map(wall => (
          <AttributeSwitchSelector
            key={wall.id}
            entityId={wall.id}
            instanceName="fullHeight"
            description={resolveLocalizable(wall.$name)}
          />
        ))}
      </PropertyContainer.Item>
    </PropertyContainer.Group>
  )
}
